/**
 * TextInput Component
 *
 * Multiline text input with four states: Default, Focus, Filled, Error.
 * - Default: empty input with placeholder
 * - Focus: active input with clear (X) button
 * - Filled: scrollable when content exceeds numberOfLines, clear button visible
 * - Error: pink background to indicate mandatory field not filled
 *
 * Uses react-native-paper TextInput as base.
 * Height is controlled by the numberOfLines prop.
 *
 * Extracted from Figma Crop Circle System.
 */

import { forwardRef, useCallback, useState } from 'react';
import {
  Platform,
  Pressable,
  TextInput as RNTextInput,
  StyleSheet,
  Text,
  View,
  type ColorValue,
  type KeyboardTypeOptions,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { TextInput as PaperTextInput } from 'react-native-paper';
import { XIcon } from '../../icons';
import { borderRadius, colors, spacing, typography } from '../../tokens';

export interface TextInputProps {
  /** Label text displayed above the input */
  label?: string;

  /** Placeholder text shown when input is empty */
  placeholder?: string;

  /** Current value of the input (controlled) */
  value?: string;

  /** Default value for uncontrolled usage */
  defaultValue?: string;

  /** Callback when text changes */
  onChangeText?: (text: string) => void;

  /** Callback when input loses focus */
  onBlur?: () => void;

  /** Callback when input gains focus */
  onFocus?: () => void;

  /** Whether the input is in an error state */
  error?: boolean;

  /** Whether the input is disabled */
  disabled?: boolean;

  /** Show the clear (X) button when the input has text */
  clearable?: boolean;

  /** Number of visible lines before scrolling */
  numberOfLines?: number;

  /** Custom style for the outer container */
  style?: ViewStyle;

  /** Custom style for the input text */
  inputStyle?: TextStyle;

  /** When true, the input auto-expands to fit content instead of scrolling */
  autoExpand?: boolean;

  /** When true, sets a minimum height of 4 lines (use with autoExpand) */
  textArea?: boolean;

  /** Auto focus the input on mount */
  autoFocus?: boolean;

  /** Custom text color (overrides default) */
  textColor?: string;

  /** Keyboard type for mobile */
  keyboardType?: KeyboardTypeOptions;

  /** Autocomplete hint */
  autoComplete?: string;

  /** Custom background color for the input container */
  backgroundColor?: ColorValue;

  /** Custom width for the wrapper */
  width?: number | '100%';

  /** Custom height (overrides numberOfLines calculation) */
  height?: number;

  /** Test ID for testing */
  testID?: string;

  /** Accessibility label */
  accessibilityLabel?: string;

  /** Hide the border (set borderWidth to 0) */
  hideBorder?: boolean;

  /** Custom style for the clear button container */
  clearButtonStyle?: ViewStyle;

  /** Custom style for the PaperTextInput.Icon (right icon) */
  rightIconStyle?: ViewStyle;
}

const LINE_HEIGHT = typography.body.small.lineHeight ?? 20;
/** Max height when autoExpand is true so placeholder/long content doesn't make the field huge */
const MAX_AUTO_EXPAND_HEIGHT = 5 * LINE_HEIGHT + (spacing['2xl'] ?? 24);

export const TextInput = forwardRef<RNTextInput, TextInputProps>(
  (
    {
      label,
      placeholder = 'Type a message...',
      value: controlledValue,
      defaultValue = '',
      onChangeText,
      onBlur: onBlurProp,
      onFocus: onFocusProp,
      error = false,
      disabled = false,
      clearable = true,
      numberOfLines,
      autoExpand = false,
      textArea = false,
      autoFocus,
      textColor,
      keyboardType,
      autoComplete,
      backgroundColor,
      width,
      height,
      style,
      inputStyle,
      testID,
      accessibilityLabel,
      hideBorder = false,
      clearButtonStyle,
      rightIconStyle,
    },
    ref
  ) => {
    const effectiveLines = numberOfLines ?? (textArea ? 4 : 1);
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [isFocused, setIsFocused] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);
    const [clearHovered, setClearHovered] = useState(false);

    const handleContentSizeChange = useCallback(
      (e: { nativeEvent: { contentSize: { height: number } } }) => {
        if (autoExpand) {
          const reported = e.nativeEvent.contentSize.height;
          setContentHeight(Math.min(reported, MAX_AUTO_EXPAND_HEIGHT));
        }
      },
      [autoExpand]
    );

    const currentValue = isControlled ? controlledValue : internalValue;
    const isFilled = currentValue.length > 0;
    const showClearButton = clearable && isFilled && !disabled;

    const handleChangeText = useCallback(
      (text: string) => {
        if (!isControlled) {
          setInternalValue(text);
        }
        onChangeText?.(text);
      },
      [isControlled, onChangeText]
    );

    const handleClear = useCallback(() => {
      if (!isControlled) {
        setInternalValue('');
      }
      onChangeText?.('');
      setClearHovered(false);
    }, [isControlled, onChangeText]);

    const getBorderColor = () => {
      if (error) return colors.semantic.errorLight;
      if (isFocused) return colors.neutral[800];
      return colors.system.borderDefault;
    };

    const handleFocus = useCallback(() => {
      setIsFocused(true);
      onFocusProp?.();
    }, [onFocusProp]);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      onBlurProp?.();
    }, [onBlurProp]);

    const getBackgroundColor = () => {
      if (error) return colors.semantic.errorLight;
      return backgroundColor ?? colors.neutral[200];
    };

    const getPlaceholderColor = () => {
      if (error) return colors.semantic.error;
      return colors.system.textPlaceholder;
    };

    const inputHeight = height ?? effectiveLines * LINE_HEIGHT + spacing['2xl'];

    const containerSizeStyle = autoExpand
      ? { minHeight: inputHeight, maxHeight: MAX_AUTO_EXPAND_HEIGHT }
      : { height: inputHeight };

    return (
      <View
        style={[styles.wrapper, width != null && { width }, style]}
        testID={testID}
      >
        {label ? (
          <Text
            style={[
              styles.label,
              error && styles.labelError,
              disabled && styles.labelDisabled,
            ]}
          >
            {label}
          </Text>
        ) : null}

        <View
          style={[
            styles.inputContainer,
            {
              borderColor: getBorderColor(),
              backgroundColor: getBackgroundColor(),
              borderWidth: hideBorder ? 0 : 1,
              ...(!autoExpand && !textArea && effectiveLines === 1
                ? { justifyContent: 'center' }
                : {}),
            },
            containerSizeStyle,
          ]}
        >
          {autoExpand || (!textArea && effectiveLines === 1) ? (
            <>
              <RNTextInput
                ref={ref}
                value={currentValue}
                onChangeText={handleChangeText}
                onFocus={handleFocus}
                onBlur={handleBlur}
                {...(autoExpand && {
                  onContentSizeChange: handleContentSizeChange,
                  multiline: true,
                })}
                placeholder={placeholder}
                placeholderTextColor={getPlaceholderColor()}
                editable={!disabled}
                autoFocus={autoFocus}
                keyboardType={keyboardType}
                autoComplete={
                  autoComplete as RNTextInput['props']['autoComplete']
                }
                cursorColor={colors.orange[500]}
                selectionColor={colors.orange[500]}
                accessibilityLabel={accessibilityLabel ?? label}
                style={[
                  autoExpand ? styles.nativeInput : styles.singleLineInput,
                  autoExpand &&
                    Platform.OS === 'web' &&
                    contentHeight > 0 && { height: contentHeight },
                  textColor != null && { color: textColor },
                  disabled && styles.nativeInputDisabled,
                  inputStyle,
                ]}
              />
              {showClearButton && (
                <View
                  pointerEvents="box-none"
                  style={[
                    styles.clearButtonWrapper,
                    !autoExpand && styles.clearButtonWrapperCentered,
                  ]}
                >
                  <Pressable
                    style={[
                      styles.clearButtonInner,
                      clearHovered && styles.clearButtonHovered,
                      clearButtonStyle,
                    ]}
                    onPress={handleClear}
                    onHoverIn={() => setClearHovered(true)}
                    onHoverOut={() => setClearHovered(false)}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel="Clear text"
                  >
                    <XIcon size={10} color={colors.slate[800]} />
                  </Pressable>
                </View>
              )}
            </>
          ) : (
            <PaperTextInput
              ref={
                ref as unknown as React.ComponentProps<
                  typeof PaperTextInput
                >['ref']
              }
              mode="flat"
              value={currentValue}
              onChangeText={handleChangeText}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              placeholderTextColor={getPlaceholderColor()}
              multiline={textArea || effectiveLines > 1}
              numberOfLines={effectiveLines}
              disabled={disabled}
              autoFocus={autoFocus}
              keyboardType={keyboardType}
              autoComplete={
                autoComplete as React.ComponentProps<
                  typeof PaperTextInput
                >['autoComplete']
              }
              accessibilityLabel={accessibilityLabel ?? label}
              textColor={
                textColor ??
                (disabled ? colors.neutral[400] : colors.system.textPrimary)
              }
              underlineColor="transparent"
              activeUnderlineColor="transparent"
              contentStyle={[
                styles.content,
                // Only apply contentSingleLine if inputStyle doesn't override paddingBottom
                // Also exclude if paddingRight is 0 (which means we want icon at edge)
                !(textArea || effectiveLines > 1) &&
                  !(
                    inputStyle &&
                    ((inputStyle as any).paddingBottom === 0 ||
                      (inputStyle as any).paddingTop === 0 ||
                      (inputStyle as any).paddingRight === 0)
                  ) &&
                  styles.contentSingleLine,
                inputStyle,
              ]}
              style={styles.paperInput}
              right={
                showClearButton ? (
                  <PaperTextInput.Icon
                    testID="right-icon-adornment"
                    icon="close"
                    size={16}
                    color={colors.neutral[500]}
                    onPress={handleClear}
                    style={
                      rightIconStyle ||
                      (!(textArea || effectiveLines > 1) &&
                        styles.contentSingleLine)
                    }
                  />
                ) : undefined
              }
              theme={{
                colors: {
                  background: 'transparent',
                  surfaceVariant: 'transparent',
                },
              }}
              cursorColor={colors.orange[500]}
              selectionColor={colors.orange[500]}
            />
          )}
        </View>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  wrapper: {
    gap: spacing.xs,
  },
  label: {
    ...typography.body.small,
    fontWeight: '500',
    color: colors.system.textPrimary,
  },
  labelError: {
    color: colors.semantic.error,
  },
  labelDisabled: {
    color: colors.neutral[400],
  },
  inputContainer: {
    borderWidth: 1,
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  paperInput: {
    backgroundColor: 'transparent',
    flex: 1,
  },
  content: {
    ...typography.body.small,
    textAlignVertical: 'top',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        outlineWidth: 0,
        resize: 'none',
        width: '100%',
        height: '100%',
      } as unknown as TextStyle,
    }),
  },
  contentSingleLine: {
    textAlignVertical: 'center',
    paddingTop: 0,
    paddingBottom: 0,
  },
  singleLineInput: {
    ...typography.body.small,
    color: colors.system.textPrimary,
    paddingHorizontal: 16,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        outlineWidth: 0,
      } as unknown as TextStyle,
    }),
  },
  nativeInput: {
    ...typography.body.small,
    color: colors.system.textPrimary,
    textAlignVertical: 'top',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 40,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        resize: 'none',
      } as unknown as TextStyle,
    }),
  },
  nativeInputDisabled: {
    color: colors.neutral[400],
  },
  clearButtonWrapper: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 1,
  },
  clearButtonWrapperCentered: {
    top: 0,
    bottom: 0,
    right: 12,
    justifyContent: 'center',
  },
  clearButtonInner: {
    width: 20,
    height: 20,
    borderRadius: 99,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearButtonHovered: {
    backgroundColor: colors.slate[200],
  },
});

TextInput.displayName = 'TextInput';
